import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  Clock3,
  Loader2,
  Moon,
  Plus,
  RefreshCw,
  Sun,
  Users,
  XCircle,
} from "lucide-react";

import { toast } from "sonner";

import TeamDropdown from "../Utils/TeamDropdown";
import LoadingScreen from "../Utils/LoadingScreen";
import CreateSlotModal from "./CreateSlotModal";
import PackerSlotsTab from "./PackerSlotsTab";

import {
  CreateDriverSlotV2Modal,
  ConfigureTimeWindowModal,
} from "./SlotManagerModals";

import useGigs from "../../Sevices/useGigs";
import useDriverTeams from "../../Sevices/Driver Api/useDriverTeams";
import DriverDropdown from "../Utils/DriverDropdown";
import useDriver from "../../Sevices/Driver Api/useDriver";
import SlotManagerTabs from "./SlotManagerTabs";

export default function SlotManager() {
  const {
    fetchSlotsApi,
    deleteSlotApi,
    updateDriverSlotStatusApi,
    createSlotApiV2,
    fetchSlotsApiV2,
    assignDriverToSlotApiV2,
    insertTimeWindowConfigApi,
    fetchAllTimeWindowConfigApi,
  } = useGigs();

  const { teams = [], fetchDriverTeams } = useDriverTeams();
  const { drivers, fetchDrivers } = useDriver();
  console.log(fetchDrivers);

  const [slotsData, setSlotsData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const [slotModalOpen, setSlotModalOpen] = useState(false);
  const [slotModalOpenV2, setSlotModalOpenV2] = useState(false);

  const [categoryFilter, setCategoryFilter] = useState("");

  const [teamFilter, setTeamFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedCreateTeam, setSelectedCreateTeam] = useState("");
  const [selectedTeamBySlot, setSelectedTeamBySlot] = useState({});

  const getTodayDate = () => {
    const today = new Date();

    const year = today.getFullYear();

    const month = String(today.getMonth() + 1).padStart(2, "0");

    const day = String(today.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const [selectedDate, setSelectedDate] = useState(getTodayDate);

  const [appliedDate, setAppliedDate] = useState(getTodayDate);
  const [selectedDateV2, setSelectedDateV2] = useState(getTodayDate);

  const [activeTab, setActiveTab] = useState("packkySlots");

  const [deletingSlotId, setDeletingSlotId] = useState(null);

  const [updatingSlotId, setUpdatingSlotId] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);

  const [selectedDriverBySlot, setSelectedDriverBySlot] = useState({});
  const [assigningSlotId, setAssigningSlotId] = useState(null);
  const [assignOpenSlotId, setAssignOpenSlotId] = useState(null);
  const [selectedDriverCodeBySlot, setSelectedDriverCodeBySlot] = useState({});
  const [saving, setSaving] = useState(false);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);

  const [timeWindowConfigs, setTimeWindowConfigs] = useState([]);

  const [configForm, setConfigForm] = useState({
    startTime: "",
    endTime: "",
    teamId: "",
    slotDurationMinutes: "",
    maxDriverCapacity: "",
    isActive: 1,
  });

  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [filteredConfigs, setFilteredConfigs] = useState([]);

  const itemsPerPage = 10;

  const lastV2FetchKeyRef = useRef("");

  const paginatedData = useMemo(() => {
    return filteredData.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage,
    );
  }, [filteredData, currentPage]);

  const windowsPerPage = 6; // number of time-window cards shown per page in V2

  const groupedByWindowFull = useMemo(() => {
    return filteredData.reduce((acc, slot) => {
      const key = slot?.windowName || "Time Window";
      if (!acc[key]) acc[key] = [];
      acc[key].push(slot);
      return acc;
    }, {});
  }, [filteredData]);

  const allWindowNames = useMemo(
    () => Object.keys(groupedByWindowFull),
    [groupedByWindowFull],
  );

  const totalPages =
    activeTab === "ordersV2"
      ? Math.max(1, Math.ceil(allWindowNames.length / windowsPerPage))
      : Math.max(1, Math.ceil(filteredData.length / itemsPerPage));

  const paginatedWindowNames = useMemo(() => {
    return allWindowNames.slice(
      (currentPage - 1) * windowsPerPage,
      currentPage * windowsPerPage,
    );
  }, [allWindowNames, currentPage]);

  const handleCreateDriverSlotV2 = async () => {
    if (!selectedDateV2) {
      toast.error("Please select date");
      return;
    }

    if (!selectedCreateTeam) {
      toast.error("Please select team");
      return;
    }

    await createDriverSlotV2(selectedDateV2, selectedCreateTeam, async () => {
      setSlotModalOpenV2(false);

      setSelectedDate(selectedDateV2);
      setAppliedDate(selectedDateV2);

      await refreshDriverSlotsV2(selectedDateV2, selectedCreateTeam);
    });
  };

  const handleDeleteSlot = async (slotId) => {
    if (!slotId || deletingSlotId) return;

    setDeletingSlotId(String(slotId));

    try {
      await deleteSlotApi(slotId, async () => {
        await refreshDriverSlots(appliedDate);
      });
    } finally {
      setDeletingSlotId(null);
    }
  };
  const [selectedWindow, setSelectedWindow] = useState(null);

  useEffect(() => {
    if (activeTab !== "ordersV2") return;

    if (!paginatedWindowNames || paginatedWindowNames.length === 0) {
      setSelectedWindow(null);
      return;
    }

    const firstWindowName = paginatedWindowNames[0];

    setSelectedWindow((prev) =>
      !prev || !paginatedWindowNames.includes(prev) ? firstWindowName : prev,
    );
  }, [paginatedWindowNames, activeTab]);

  const getTeamName = useCallback(
    (teamId) => {
      const team = teams.find((item) => String(item?.id) === String(teamId));

      return team?.team_name || team?.teamName || `Team ${teamId}`;
    },
    [teams],
  );

  const getSlotId = useCallback((slot) => {
    return String(
      slot?.slotId ||
        slot?.pk_slot_id ||
        slot?.slot_id ||
        slot?.id ||
        slot?.raw?.pk_slot_id ||
        "",
    ).trim();
  }, []);

  const formatDateTime = (value) => {
    if (!value) return "N/A";

    const normalizedValue = String(value).trim();

    const safeValue = normalizedValue.includes(" ")
      ? normalizedValue.replace(" ", "T")
      : normalizedValue;

    const parsedDate = new Date(safeValue);

    if (Number.isNaN(parsedDate.getTime())) {
      return normalizedValue;
    }

    return parsedDate.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getCategoryIcon = (category) => {
    const normalized = String(category || "").toLowerCase();

    if (normalized.includes("night") || normalized.includes("dinner")) {
      return <Moon size={15} />;
    }

    return <Sun size={15} />;
  };

  const refreshDriverSlots = useCallback(
    async (date = appliedDate) => {
      return fetchSlotsApi(date, setSlotsData, setFilteredData, setLoading);
    },
    [fetchSlotsApi, appliedDate],
  );
  const refreshDriverSlotsV2 = useCallback(
    async (date, teamId) => {
      return fetchSlotsApiV2(
        date,
        teamId,
        setSlotsData,
        setFilteredData,
        setLoading,
      );
    },
    [fetchSlotsApiV2],
  );

  const createDriverSlotV2 = useCallback(
    async (date, teamId, onSuccess) => {
      return createSlotApiV2(date, teamId, setLoading, onSuccess);
    },
    [createSlotApiV2],
  );
  // OLD Driver Slots API
  useEffect(() => {
    if (activeTab !== "orders") return;

    fetchDriverTeams();
    refreshDriverSlots(appliedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, appliedDate]);

  // V2 Driver Slots API
  useEffect(() => {
    if (activeTab !== "ordersV2") {
      lastV2FetchKeyRef.current = "";
      return;
    }
    fetchDriverTeams();
    const fetchKey = `v2-${appliedDate}-${teamFilter}`;

    // Prevent the same date from being fetched repeatedly
    if (lastV2FetchKeyRef.current === fetchKey) {
      return;
    }

    lastV2FetchKeyRef.current = fetchKey;

    fetchSlotsApiV2(
      appliedDate,
      teamFilter,
      setSlotsData,
      setFilteredData,
      setLoading,
    );

    // fetchSlotsApiV2 intentionally excluded because
    // useGigs may return a new function reference on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, appliedDate, teamFilter]);

  const handleApplyDate = async () => {
    if (!selectedDate) {
      toast.error("Please select a date");
      return;
    }

    setAppliedDate(selectedDate);
    setCurrentPage(1);

    await refreshDriverSlots(selectedDate);
  };
  const handleApplyDateV2 = async () => {
    if (!selectedDate) {
      toast.error("Please select a date");
      return;
    }

    setAppliedDate(selectedDate);
    setCurrentPage(1);

    await refreshDriverSlotsV2(selectedDate, teamFilter);
  };
  useEffect(() => {
    let filtered = [...slotsData];

    if (appliedDate) {
      filtered = filtered.filter((slot) => {
        const slotDate = String(slot?.fromDate || slot?.from_date || "").slice(
          0,
          10,
        );

        return slotDate === appliedDate;
      });
    }

    const query = searchQuery.trim().toLowerCase();

    if (query) {
      filtered = filtered.filter((slot) => {
        const searchableText = [
          slot?.category,
          slot?.teamId,
          getTeamName(slot?.teamId),
          slot?.fromDate,
          slot?.toDate,
        ]
          .join(" ")
          .toLowerCase();

        return searchableText.includes(query);
      });
    }

    if (categoryFilter) {
      filtered = filtered.filter(
        (slot) =>
          String(slot?.category || "").toLowerCase() ===
          categoryFilter.toLowerCase(),
      );
    }

    if (teamFilter) {
      filtered = filtered.filter(
        (slot) => String(slot?.teamId) === String(teamFilter),
      );
    }

    if (statusFilter) {
      filtered = filtered.filter((slot) => {
        if (statusFilter === "active") {
          return slot?.isActive;
        }

        if (statusFilter === "inactive") {
          return !slot?.isActive;
        }

        return true;
      });
    }

    setFilteredData(filtered);
    setCurrentPage(1);
  }, [
    searchQuery,
    categoryFilter,
    teamFilter,
    statusFilter,
    slotsData,
    getTeamName,
    appliedDate,
  ]);

  const categoryOptions = useMemo(() => {
    return Array.from(
      new Set(
        slotsData
          .map((slot) => String(slot?.category || "").trim())
          .filter(Boolean),
      ),
    );
  }, [slotsData]);

  useEffect(() => {
    fetchDrivers();
  }, []);

  const handleAssignDriver = async (slot, driverCode) => {
    const slotId = getSlotId(slot);
    setSaving(true);
    try {
      await assignDriverToSlotApiV2(slotId, driverCode);
      await refreshDriverSlotsV2(appliedDate, teamFilter); // refresh paginatedData / driver counts
      setAssignOpenSlotId(null);
      setSelectedDriverCodeBySlot((prev) => ({ ...prev, [slotId]: "" }));
    } catch (err) {
      // surface an error toast as appropriate
    } finally {
      setSaving(false);
    }
  };

  const fetchTimeWindowConfigs = useCallback(async () => {
    return fetchAllTimeWindowConfigApi(
      "",
      setTimeWindowConfigs,
      setConfigLoading,
    );
  }, [fetchAllTimeWindowConfigApi]);

  const handleOpenConfigModal = () => {
    setEditingConfig(null);

    setConfigForm({
      startTime: "",
      endTime: "",
      teamId: "",
      slotDurationMinutes: "",
      maxDriverCapacity: "",
      isActive: 1,
    });

    setConfigModalOpen(true);
  };

  const handleConfigChange = (field, value) => {
    setConfigForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveConfig = async () => {
    if (!configForm.startTime) {
      toast.error("Start time is required");
      return;
    }

    if (!configForm.endTime) {
      toast.error("End time is required");
      return;
    }

    if (!configForm.teamId) {
      toast.error("Team is required");
      return;
    }

    if (!configForm.slotDurationMinutes) {
      toast.error("Slot duration is required");
      return;
    }

    if (!configForm.maxDriverCapacity) {
      toast.error("Maximum driver capacity is required");
      return;
    }

    if (configForm.endTime <= configForm.startTime) {
      toast.error("End time must be later than start time");
      return;
    }

    const success = await insertTimeWindowConfigApi(
      configForm,
      setConfigSaving,
      async () => {
        setConfigModalOpen(false);

        await fetchTimeWindowConfigs();
      },
    );

    return success;
  };

  useEffect(() => {
    if (activeTab !== "configure") return;

    fetchDriverTeams();
    fetchTimeWindowConfigs();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    let filtered = [...timeWindowConfigs];

    const query = searchQuery.trim().toLowerCase();

    if (query) {
      filtered = filtered.filter((config) => {
        const searchableText = [
          config?.windowName,
          config?.startTime,
          config?.endTime,
          config?.teamId,
          getTeamName(config?.teamId),
          config?.slotDurationMinutes,
          config?.maxDriverCapacity,
        ]
          .join(" ")
          .toLowerCase();

        return searchableText.includes(query);
      });
    }

    if (categoryFilter) {
      filtered = filtered.filter(
        (config) =>
          String(config?.category || "").toLowerCase() ===
          categoryFilter.toLowerCase(),
      );
    }

    if (teamFilter) {
      filtered = filtered.filter(
        (config) => String(config?.teamId) === String(teamFilter),
      );
    }

    if (statusFilter) {
      filtered = filtered.filter((config) => {
        const isActive =
          String(config?.isActive) === "1" ||
          config?.isActive === true ||
          String(config?.isActive).toLowerCase() === "true";

        if (statusFilter === "active") {
          return isActive;
        }

        if (statusFilter === "inactive") {
          return !isActive;
        }

        return true;
      });
    }

    setFilteredConfigs(filtered);
  }, [
    timeWindowConfigs,
    searchQuery,
    categoryFilter,
    teamFilter,
    statusFilter,
    getTeamName,
  ]);
  const handleEditConfig = (config) => {
    setEditingConfig(config);

    setConfigForm({
      startTime: config?.startTime || "",
      endTime: config?.endTime || "",
      teamId: config?.teamId || "",
      slotDurationMinutes: config?.slotDurationMinutes || "",
      maxDriverCapacity: config?.maxDriverCapacity || "",
      isActive:
        String(config?.isActive) === "1" ||
        config?.isActive === true ||
        String(config?.isActive).toLowerCase() === "true"
          ? 1
          : 0,
    });

    setConfigModalOpen(true);
  };
  return (
    <div className="flex h-full flex-col overflow-hidden p-4 sm:p-5">
      <div className="sticky top-0 z-30 shrink-0">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-xl font-bold text-gray-800">Slots Manager</h1>

          {activeTab === "ordersV2" && (
            <div className="flex w-full flex-wrap items-center gap-2 lg:w-auto">
              <div className="flex min-w-[230px] flex-1 items-center gap-2 sm:flex-none">
                <input
                  type="date"
                  min={getTodayDate()}
                  value={selectedDate}
                  onChange={(event) => setSelectedDate(event.target.value)}
                  className="h-10 min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                />

                <button
                  type="button"
                  onClick={handleApplyDateV2}
                  disabled={loading || !selectedDate}
                  className="inline-flex h-10 items-center justify-center rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader2 size={15} className="mr-1.5 animate-spin" />
                      Applying...
                    </>
                  ) : (
                    "Apply"
                  )}
                </button>
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search slots..."
                className="h-10 min-w-[180px] flex-1 rounded-lg border border-gray-300 px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 sm:flex-none"
              />

              {/* <div className="min-w-[150px] flex-1 sm:flex-none">
                <TeamDropdown
                  teams={teams}
                  value={teamFilter}
                  onChange={setTeamFilter}
                  placeholder="All Teams"
                />
              </div> */}

              {/* <select
                value={categoryFilter}
                onChange={(event) => setCategoryFilter(event.target.value)}
                className="h-10 min-w-[140px] flex-1 rounded-lg border border-gray-300 bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 sm:flex-none"
              >
                <option value="">All Categories</option>

                {categoryOptions.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select> */}

              {/* <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                className="h-10 min-w-[130px] flex-1 rounded-lg border border-gray-300 bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 sm:flex-none"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select> */}

              <button
                type="button"
                onClick={() => refreshDriverSlotsV2(appliedDate, teamFilter)}
                disabled={loading}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <RefreshCw size={16} />
                )}
                Refresh
              </button>

              <button
                type="button"
                onClick={() => {
                  setSelectedDateV2(getTodayDate());
                  setSlotModalOpenV2(true);
                }}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-orange-500 px-4 text-sm font-medium text-white shadow-sm hover:bg-orange-600"
              >
                <Plus size={16} />
                Create Slot
              </button>
            </div>
          )}
          {activeTab === "configure" && (
            <div className="flex w-full flex-wrap items-center gap-2 lg:w-auto">
              <div className="flex min-w-[230px] flex-1 items-center gap-2 sm:flex-none">
                <input
                  type="date"
                  min={getTodayDate()}
                  value={selectedDate}
                  onChange={(event) => setSelectedDate(event.target.value)}
                  className="h-10 min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                />

                <button
                  type="button"
                  onClick={handleApplyDate}
                  disabled={loading || !selectedDate}
                  className="inline-flex h-10 items-center justify-center rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader2 size={15} className="mr-1.5 animate-spin" />
                      Applying...
                    </>
                  ) : (
                    "Apply"
                  )}
                </button>
              </div>

              <input
                type="text"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search slots..."
                className="h-10 min-w-[180px] flex-1 rounded-lg border border-gray-300 px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 sm:flex-none"
              />

              <div className="min-w-[150px] flex-1 sm:flex-none">
                <TeamDropdown
                  teams={teams}
                  value={teamFilter}
                  onChange={setTeamFilter}
                  placeholder="All Teams"
                />
              </div>

              <select
                value={categoryFilter}
                onChange={(event) => setCategoryFilter(event.target.value)}
                className="h-10 min-w-[140px] flex-1 rounded-lg border border-gray-300 bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 sm:flex-none"
              >
                <option value="">All Categories</option>

                {categoryOptions.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>

              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                className="h-10 min-w-[130px] flex-1 rounded-lg border border-gray-300 bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 sm:flex-none"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>

              <button
                type="button"
                onClick={() => fetchTimeWindowConfigs()}
                disabled={loading}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <RefreshCw size={16} />
                )}
                Refresh
              </button>

              <button
                type="button"
                onClick={handleOpenConfigModal}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-orange-500 px-4 text-sm font-medium text-white shadow-sm hover:bg-orange-600"
              >
                <Plus size={16} />
                Configure Slot
              </button>
            </div>
          )}
        </div>

        <div className="mb-5 border-b border-gray-200">
          <div className="flex items-center gap-9">
            {[
              {
                key: "packkySlots",
                label: "Packky Slots",
              },
              {
                key: "ordersV2",
                label: "Drivers Slots",
              },
              {
                key: "configure",
                label: "Configure Slots",
              },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`relative min-w-[70px] px-1 pb-3 pt-2 text-left text-sm font-medium transition ${
                  activeTab === tab.key
                    ? "text-orange-600"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                {tab.label}

                {activeTab === tab.key && (
                  <span className="absolute bottom-[-1px] left-0 h-[2px] w-full rounded-full bg-orange-500" />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto">
          {activeTab === "packkySlots" && <PackerSlotsTab />}

          <SlotManagerTabs
            activeTab={activeTab}
            loading={loading}
            paginatedData={paginatedData}
            selectedDate={selectedDate}
            groupedByWindowFull={groupedByWindowFull}
            paginatedWindowNames={paginatedWindowNames}
            selectedWindow={selectedWindow}
            setSelectedWindow={setSelectedWindow}
            getSlotId={getSlotId}
            formatDateTime={formatDateTime}
            drivers={drivers}
            selectedDriverCodeBySlot={selectedDriverCodeBySlot}
            setSelectedDriverCodeBySlot={setSelectedDriverCodeBySlot}
            saving={saving}
            handleAssignDriver={handleAssignDriver}
            setAssignOpenSlotId={setAssignOpenSlotId}
            totalPages={totalPages}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            configLoading={configLoading}
            filteredConfigs={filteredConfigs}
            getTeamName={getTeamName}
            handleEditConfig={handleEditConfig}
          />
        </div>
      </div>

      {slotModalOpen && (
        <CreateSlotModal
          isOpen={slotModalOpen}
          onClose={() => setSlotModalOpen(false)}
          onCreated={async () => {
            await refreshDriverSlots(appliedDate);
          }}
        />
      )}

      <CreateDriverSlotV2Modal
        open={slotModalOpenV2}
        onOpenChange={setSlotModalOpenV2}
        selectedDate={selectedDateV2}
        onDateChange={setSelectedDateV2}
        minDate={getTodayDate()}
        teams={teams}
        selectedTeam={selectedCreateTeam}
        onTeamChange={setSelectedCreateTeam}
        onCreate={handleCreateDriverSlotV2}
        loading={loading}
      />

      <ConfigureTimeWindowModal
        open={configModalOpen}
        onOpenChange={setConfigModalOpen}
        editingConfig={editingConfig}
        configForm={configForm}
        onConfigChange={handleConfigChange}
        teams={teams}
        saving={configSaving}
        onSave={handleSaveConfig}
      />
    </div>
  );
}
